import { javaServerApiBrowserAxiosUrl } from "@repo/config";
import type { User } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

import { ADMIN_CLINICS_COLLECTION } from "../../constants/collections/index.js";
import { OnboardingCurrentStep } from "../../enums/company.js";
import { createApiClient } from "../apiClient.js";
import { db } from "../firebase.js";

const clinicKrJavaServerApiClient = createApiClient({
  baseURL: javaServerApiBrowserAxiosUrl,
  teamType: "clinic",
});

type ClinicClientKrAccountData = {
  id?: number | null;
  state?: number | null;
  isRegisterNurse?: boolean;
  company?: {
    id?: number | null;
    kind?: number | null;
    currentPlan?: number | null;
    withdrawal?: number | null;
  } | null;
};

export type ClinicClientKrCompatibleAuthState = {
  user: User;
  rdsCompanyId: number | null;
  rdsAccountId: number | null;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const normalizeClinicClientKrLoginIdentifier = (identifier: string) => {
  const loweredIdentifier = identifier.trim().toLowerCase();

  if (EMAIL_PATTERN.test(loweredIdentifier)) {
    return loweredIdentifier;
  }

  const withDomain = `noemail-${loweredIdentifier}@gloud.io`;
  if (EMAIL_PATTERN.test(withDomain)) {
    return withDomain;
  }

  const encoded = encodeURIComponent(loweredIdentifier).replace(/%/g, "_");
  const encodedIdentifier = `noemail-${encoded}@gloud.io`;
  if (EMAIL_PATTERN.test(encodedIdentifier)) {
    return encodedIdentifier;
  }

  return identifier;
};

const hasClinicClientKrAccess = async (
  accountData: ClinicClientKrAccountData,
) => {
  if (!accountData?.company?.id) {
    return false;
  }

  if (accountData.state === 50) {
    return false;
  }

  const isBlockedNurseAccount = accountData.state === 40;

  if (accountData.isRegisterNurse) {
    return !isBlockedNurseAccount;
  }

  // Source of truth is Firestore adminClinicsV2.currentStatus, not the legacy
  // withdrawal/currentPlan fields. "withdrawal" and missing/undefined
  // intentionally pass through as having access - no decided UX for
  // withdrawn clinics yet, so we don't gate them here. A failed read also
  // passes through rather than denying access on a transient error.
  let currentStatus: string | undefined;
  try {
    const clinicDoc = await getDoc(
      doc(db, ADMIN_CLINICS_COLLECTION, String(accountData.company.id)),
    );
    currentStatus = clinicDoc.data()?.currentStatus;
  } catch (error) {
    console.error("클리닉 인증 상태 조회 중 오류 발생:", error);
  }

  return currentStatus !== "pending";
};

const hasCompletedClinicClientKrOnboarding = async (companyId: number) => {
  try {
    const onboardingSnapshot = await getDoc(
      doc(db, ADMIN_CLINICS_COLLECTION, String(companyId)),
    );
    if (!onboardingSnapshot.exists()) {
      return true;
    }

    const currentStep =
      onboardingSnapshot.data()?.currentStep || OnboardingCurrentStep.COMPLETED;

    return currentStep === OnboardingCurrentStep.COMPLETED;
  } catch (error) {
    console.error("온보딩 단계 조회 중 오류 발생:", error);
    return true;
  }
};

export const resolveClinicClientKrCompatibleAuthState = async (
  user: User,
): Promise<ClinicClientKrCompatibleAuthState | null> => {
  try {
    const accountData =
      (await clinicKrJavaServerApiClient.get("/api/v1/accounts")).data
        ?.result ?? null;

    if (!accountData || !(await hasClinicClientKrAccess(accountData))) {
      return null;
    }

    const companyId = accountData.company?.id ?? null;
    if (!companyId) {
      return null;
    }

    const hasCompletedOnboarding =
      await hasCompletedClinicClientKrOnboarding(companyId);

    if (!hasCompletedOnboarding) {
      return null;
    }

    return {
      user,
      rdsCompanyId: companyId,
      rdsAccountId: accountData.id ?? null,
    };
  } catch (error) {
    console.error("clinic-client-kr 권한 확인 중 오류 발생:", error);
    return null;
  }
};
